import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { isMouseRoute, mousePose, mousePlaybackRate } from './mouseMotion';
import './ChristmasMouse.css';

const ASSETS = `${process.env.PUBLIC_URL || ''}/christmas-mouse`;
const STORAGE_KEY = 'belvoir-mouse-paused';

// Blender-rendered transparent sprite sheets preserve the actual model without
// adding a permanent WebGL renderer to every page of the marketing website.
export default function ChristmasMouse({ logoRef, menuOpen, enabled = true }) {
  const { pathname } = useLocation();
  const navigate=useNavigate();
  const [reacting,setReacting]=useState(false);
  const lastPose=useRef(null);
  const reaction=useRef(null);
  const layerRef = useRef(null);
  const spriteRef = useRef(null);
  const clockRef = useRef(0);
  const [ready, setReady] = useState(false);
  const [reduced, setReduced] = useState(true);
  const [paused, setPaused] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const eligible = enabled && isMouseRoute(pathname);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update(); query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!eligible || reduced || ready) return undefined;
    let cancelled = false;
    const images = ['run.webp', 'peek.webp'].map((name) => new Image());
    Promise.all(images.map((image, i) => new Promise((resolve, reject) => {
      image.onload = resolve; image.onerror = reject;
      image.src = `${ASSETS}/${i === 0 ? 'run' : 'peek'}.webp`;
    }))).then(() => { if (!cancelled) setReady(true); }).catch(() => { /* Decoration fails silently. */ });
    return () => { cancelled = true; images.forEach((im) => { im.onload = null; im.onerror = null; }); };
  }, [eligible, reduced, ready]);

  useEffect(()=>{
    if(!reacting)return undefined;
    const timer=setTimeout(()=>{setReacting(false);reaction.current=null;navigate('/christmas/mice-trail');},1400);
    return()=>clearTimeout(timer);
  },[reacting,navigate]);
  const followMouse=()=>{
    if(reacting)return;
    reaction.current={...lastPose.current,elapsed:0};setReacting(true);
  };
  const active = eligible && ready && !reduced;
  useEffect(() => {
    if (!active || paused || menuOpen) return undefined;
    let raf = 0;
    let last = 0;
    let layout;
    let modalOpen = false;
    const measure = () => {
      const node = logoRef.current;
      if (!node) { layout = null; return; }
      const rect = node.getBoundingClientRect();
      layout = {
        width: window.innerWidth, height: window.innerHeight,
        size: window.innerWidth < 768 ? 54 : 72,
        logo: { left: rect.left, right: rect.right, bottom: rect.bottom },
      };
    };
    const hide = () => { if (layerRef.current) layerRef.current.style.visibility = 'hidden'; };
    const tick = (now) => {
      const dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
      last = now;
      if (!layout || modalOpen || document.hidden) { hide(); }
      else {
        const currentPose=mousePose(clockRef.current,layout);
        clockRef.current += dt*(currentPose.kind==='run'?mousePlaybackRate(layout):1);
        let p = mousePose(clockRef.current, layout);
        if(reaction.current){
          const r=reaction.current;r.elapsed+=dt;const t=r.elapsed;
          p={...r,visible:true,clip:0,clipRight:0,kind:t<.65?'peek':'run',facing:1,
            x:r.x+Math.max(0,t-.65)*layout.width*1.6,
            y:r.y-Math.sin(Math.min(1,t/.65)*Math.PI)*36};
        }
        lastPose.current=p;
        const layer = layerRef.current;
        const sprite = spriteRef.current;
        if (layer && sprite) {
          layer.style.visibility = p.visible ? 'visible' : 'hidden';
          layer.style.clipPath = `inset(0 ${p.clipRight}px 0 ${p.clip}px)`;
          const frames = p.kind === 'run' ? 12 : 8;
          const frame = Math.floor(clockRef.current / (p.kind === 'run' ? 0.05 : 0.15)) % frames;
          sprite.style.width = `${layout.size}px`;
          sprite.style.height = `${layout.size * 280 / 240}px`;
          sprite.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) scaleX(${p.facing})`;
          sprite.style.backgroundImage = `url("${ASSETS}/${p.kind}.webp")`;
          sprite.style.backgroundSize = `${layout.size * frames}px ${layout.size * 280 / 240}px`;
          sprite.style.backgroundPosition = `${-frame * layout.size}px 0`;
          sprite.dataset.pose = p.kind;
          sprite.dataset.phase = p.phase || p.kind;
        }
      }
      if (!document.hidden) raf = requestAnimationFrame(tick);
    };
    const visibility = () => {
      cancelAnimationFrame(raf); last = 0;
      if (document.hidden) hide(); else { measure(); raf = requestAnimationFrame(tick); }
    };
    const onEscape = (event) => { if (event.key === 'Escape') setPaused(true); };
    const checkModal = () => { modalOpen = !!document.querySelector('[aria-modal="true"], dialog[open]'); };
    const observer = new MutationObserver(checkModal);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-modal', 'open'] });
    const resize = new ResizeObserver(measure);
    if (logoRef.current) resize.observe(logoRef.current);
    measure(); checkModal(); raf = requestAnimationFrame(tick);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('keydown', onEscape);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      cancelAnimationFrame(raf); hide(); observer.disconnect(); resize.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('keydown', onEscape);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [active, paused, menuOpen, pathname, logoRef]);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, paused ? '1' : '0'); } catch { /* Storage may be unavailable. */ }
  }, [paused]);

  if (!active || menuOpen) return null;
  return createPortal(
    <>
      {!paused && (
        <div className="belvoir-mouse-layer" ref={layerRef}>
          <button type="button" className="belvoir-mouse-sprite" ref={spriteRef} onClick={followMouse} disabled={reacting} aria-label="Follow the Christmas mouse to the mice trail" title="The mice are coming to five towns — follow me!" />
        </div>
      )}
      <button
        type="button"
        className="belvoir-mouse-toggle"
        onClick={() => setPaused((value) => !value)}
        aria-label={paused ? 'Play Christmas mouse animation' : 'Pause Christmas mouse animation'}
        title={paused ? 'Let the Christmas mouse play' : 'Pause the Christmas mouse'}
      >
        <span aria-hidden="true">{paused ? '▶' : 'Ⅱ'}</span>
        <span className="belvoir-mouse-toggle-label">Christmas mouse</span>
      </button>
    </>, document.body
  );
}
